import { type ReactNode } from "react";
import { FilterIcon } from "vega-icons";

import {
  ThemedButton,
  type ThemedButtonProps,
} from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { CounterBadge } from "@vega-core/components/CounterBadge/CounterBadge";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import { SidebarModalV2 } from "../SidebarModal/SidebarModalV2";

export type FiltersModalProps = {
  children?: ReactNode;
  dataTestId?: string;
  onClose?: () => void;
  actionPanelActions?: {
    onPrimaryAction: () => void;
    onSecondaryAction: () => void;
    primaryActionProps?: Partial<ThemedButtonProps>;
    secondaryActionProps?: Partial<ThemedButtonProps>;
  };
  filterCount?: number;
  isLoading?: boolean;
  disabled?: boolean;
};

export const FiltersSideDrawer = ({
  children,
  dataTestId = "filters-modal",
  actionPanelActions,
  onClose,
  filterCount,
  isLoading,
  disabled = false,
}: FiltersModalProps) => {
  const sideBarModal = useBoolean(false);
  const setIsOpen = (isOpen: boolean) => {
    if (isOpen) {
      sideBarModal.setTrue();
    } else {
      sideBarModal.setFalse();
      onClose?.();
    }
  };

  const handlePrimaryAction = () => {
    actionPanelActions?.onPrimaryAction();
    sideBarModal.setFalse();
  };

  const handleSecondaryAction = () => {
    actionPanelActions?.onSecondaryAction();
    sideBarModal.setFalse();
  };

  return (
    <>
      <div className="flex items-center gap-2 relative">
        <ThemedIconButton
          ariaLabel="filters-modal"
          dataTestId={dataTestId}
          disabled={disabled}
          icon={<FilterIcon />}
          isLoading={isLoading}
          onClick={sideBarModal.setTrue}
          size="large"
        />
        {filterCount && filterCount > 0 ? (
          <CounterBadge
            className="absolute -top-2.5 -right-2.5 scale-75 bg-background/primary shadow-card text-button/secondary/label/default border border-button/secondary/border/default body-base-semibold"
            count={filterCount}
            variant="rounded"
          />
        ) : null}
      </div>
      <SidebarModalV2
        actionPanel={
          <div className="flex items-center gap-2 w-full">
            <ThemedButton
              dataTestId="reset-filters"
              fullWidth
              variant="secondary"
              {...actionPanelActions?.secondaryActionProps}
              onClick={handleSecondaryAction}
            >
              {m.btn_clearFilters()}
            </ThemedButton>
            <ThemedButton
              dataTestId="apply-filters"
              fullWidth
              variant="primary"
              {...actionPanelActions?.primaryActionProps}
              onClick={handlePrimaryAction}
            >
              {m.btn_applyFilters()}
            </ThemedButton>
          </div>
        }
        dataTestId={dataTestId}
        isOpen={sideBarModal.isOpen}
        setIsOpen={setIsOpen}
        title={m.common_filters()}
      >
        {children}
      </SidebarModalV2>
    </>
  );
};
